import React from 'react';
import { IDeveloperProfile } from 'utils/entities';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { SmallText } from 'ds/components/typography';
import { MOBILE_LOGO_SIZE, DESKTOP_LOGO_SIZE } from 'screens/DeveloperPage/consts';
import { SectionWrapper } from 'screens/DeveloperPage/styled';
import LogoIcon from 'assets/svg/developer/suitcase.svg';
import LocationIcon from 'assets/svg/developer/location.svg';
import PhoneIcon from 'assets/svg/phone-1-5.svg';
import Contact from 'screens/DeveloperPage/components/contact';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { Share } from './Share';
import * as Styled from './styled';
import { useLocale } from 'locale';
import { getUrbanRenewalLink } from 'utils/urban-renewal-links';


interface BusinessCardProps {
  showLogo: boolean;
  showAddress: boolean;
  showPhoneNumber: boolean;
  developer: IDeveloperProfile;
  collapsed: boolean;
  collapsedBGImg: string;
  showContact: boolean;
  greyBg: boolean;
  onCallClick: () => void;
}

const getGapSize = (hasAddress: boolean, hasPhone: boolean, hasRenewalLink: boolean) => {
  switch (true) {
    case hasAddress && hasPhone:
      return 4;
    case hasAddress || hasPhone || hasRenewalLink:
      return 8;
    default:
      return 16;
  }
};

const BusinessCard: React.FC<BusinessCardProps> = (props) => {
  const {
    showPhoneNumber,
    collapsedBGImg,
    showAddress,
    showLogo,
    developer,
    collapsed,
    showContact,
    greyBg,
    onCallClick,
  } = props;
  const { phoneNumber, address, logoUrl, name } = developer;
  const { t } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const isMobile = useScreenBreakpoint() === 1;
  const logoSize = isMobile ? MOBILE_LOGO_SIZE : DESKTOP_LOGO_SIZE;
  const InfoLine = isMobile ? SmallText : Styled.InfoLine;
  const hasLogo = Boolean(showLogo && logoUrl);
  const collapsedProp = isMobile ? null : { collapsed };
  const collapsedBg = collapsedBGImg ? constructImageURI(collapsedBGImg, { width: 800 }) : null;
  const hasCollapsedBgProp = { hasCollapsedBg: !!collapsedBg };
  const imageUrl = constructImageURI(logoUrl, { width: logoSize, fit: 'contain' });

  const hasAddress = showAddress && address && !collapsed;
  const hasPhone = Boolean(showPhoneNumber && phoneNumber);
  const urbanRenewalLink = getUrbanRenewalLink(developer.id);
  const hasRenewalLink = !!urbanRenewalLink;

  const phoneLink = (
    <Styled.PhoneLink href={createPhoneURI(phoneNumber)} onClick={onCallClick}>
      <InfoLine {...collapsedProp} {...hasCollapsedBgProp} data-auto="developer-phone">
        <ShowAt from={2}>
          <PhoneIcon />
        </ShowAt>
        {phoneNumber}
      </InfoLine>
    </Styled.PhoneLink>
  );

  const shareButton = (
    <ShowAt from={2}>
      <Styled.ExpandedShareWrapper hidden={collapsed}>
        <Share developer={developer} />
      </Styled.ExpandedShareWrapper>
    </ShowAt>
  );

  return (
    <Styled.Wrapper {...collapsedProp} {...hasCollapsedBgProp} greyBg={greyBg}>
      <ShowAt from={2}>
        {collapsed && collapsedBg ? (
          <Styled.CollapsedBG src={collapsedBg}/>
        ) : null}
      </ShowAt>
      <SectionWrapper noTopMargin>
        <Styled.Inner {...collapsedProp}>
          <Styled.LogoWrapper hasLogo={hasLogo} {...collapsedProp}>
            <Styled.LogoInner {...collapsedProp} >
              {hasLogo ? (
                <Styled.LogoImg src={imageUrl} alt={developer.name} />
              ) : (
                <LogoIcon />
              )}
            </Styled.LogoInner>
          </Styled.LogoWrapper>
          <Styled.ContentWrapper>
            <Styled.InfoWrapper {...collapsedProp} gapSize={getGapSize(hasAddress, hasPhone, hasRenewalLink)}>
              <Styled.NameLine {...collapsedProp} weight={collapsed ? 'medium' : 'bold'} data-auto="developer-title">
                {name}
              </Styled.NameLine>
              {!collapsed && hasRenewalLink ? (
                <Styled.UrbanRenewalLink href={urbanRenewalLink} target="_blank" rel="noopener noreferrer">
                  {t('developerPage.urbanRenewalLinkText')}
                </Styled.UrbanRenewalLink>
              ) : null}
              <Styled.CollapsedShareWrapper {...collapsedProp} {...hasCollapsedBgProp}>
                <Share iconOnly developer={developer} />
              </Styled.CollapsedShareWrapper>
              {hasAddress ? (
                <InfoLine>
                  <ShowAt from={2}>
                    <LocationIcon />
                  </ShowAt>
                  {address}
                </InfoLine>
              ) : null}
              {hasPhone && !collapsed ? phoneLink : null}
              {!hasRenewalLink ? shareButton : null}
            </Styled.InfoWrapper>
            <Styled.ButtonsWrapper>
              <ShowAt from={2}>
                {showContact ? (
                  <Styled.ContactWrapper collapsed={collapsed}>
                    <Contact isSmallBtn={collapsed}/>
                      {collapsed && showPhoneNumber && phoneNumber ? (<Styled.ContactOrText>{t('developerPage.contactOrText')}</Styled.ContactOrText>) : null}
                      {hasPhone && collapsed ? phoneLink : null}
                  </Styled.ContactWrapper>
                ) : null}
              </ShowAt>
              {hasRenewalLink ? shareButton : null}
            </Styled.ButtonsWrapper>
          </Styled.ContentWrapper>
        </Styled.Inner>
      </SectionWrapper>
    </Styled.Wrapper>
  );
};

export default React.memo(BusinessCard);
